import React, { useState, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Upload, Loader2, CheckCircle2, XCircle, FileText, 
  Trash2, Play, Download, FolderOpen, AlertTriangle, Shield, FileStack, Files
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { exportAnalysisToPDF, exportBatchAnalysesToPDF } from "@/lib/pdfExport";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type SummaryLength = 'short' | 'medium' | 'detailed';

interface ConfidentialityWarning {
  type: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
}

interface BatchFile {
  id: string;
  file: File;
  status: 'pending' | 'processing' | 'completed' | 'error';
  progress: number;
  result?: {
    zusammenfassung: string;
    keywords: string[];
    sentiment: {
      overall: string;
      positive: number;
      neutral: number;
      negative: number;
    };
    themes: Array<{ theme: string; sentiment: string }>;
    language: string;
    category?: string;
    confidentialityWarnings?: ConfidentialityWarning[];
  };
  error?: string;
}

interface BatchUploadProps {
  apiKey: string;
  onUsageUpdate: () => void;
}

export default function BatchUpload({ apiKey, onUsageUpdate }: BatchUploadProps) {
  const [files, setFiles] = useState<BatchFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [summaryLength, setSummaryLength] = useState<SummaryLength>('medium');

  const handleFilesSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    const newFiles: BatchFile[] = Array.from(selectedFiles).map((file: File) => ({
      id: crypto.randomUUID(),
      file,
      status: 'pending' as const,
      progress: 0
    }));

    setFiles(prev => [...prev, ...newFiles]);
    toast.success(`${newFiles.length} Datei(en) hinzugefügt`);
    
    // Reset input
    e.target.value = '';
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setFiles([]);
  }, []);

  const extractTextFromFile = async (file: File): Promise<string> => {
    if (file.type === 'application/pdf') {
      // Use PDF extraction edge function
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const { data, error } = await supabase.functions.invoke('kompakt-extract-pdf', {
        body: { apiKey, base64Content: base64, fileName: file.name }
      });

      if (error) throw new Error(error.message || 'PDF extraction failed');
      return data.text;
    } else {
      // Read text file directly
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsText(file);
      });
    }
  };

  const processFile = async (batchFile: BatchFile): Promise<BatchFile> => {
    try {
      // Update status to processing
      setFiles(prev => prev.map(f => 
        f.id === batchFile.id ? { ...f, status: 'processing', progress: 10 } : f
      ));

      // Extract text
      const text = await extractTextFromFile(batchFile.file);
      
      setFiles(prev => prev.map(f => 
        f.id === batchFile.id ? { ...f, progress: 50 } : f
      ));

      // Analyze
      const { data, error } = await supabase.functions.invoke('kompakt-analyze', {
        body: { 
          apiKey, 
          text, 
          fileName: batchFile.file.name,
          summaryLength 
        }
      });

      if (error) throw new Error(error.message || 'Analysis failed');

      setFiles(prev => prev.map(f => 
        f.id === batchFile.id ? { ...f, progress: 100, status: 'completed', result: data } : f
      ));

      return { ...batchFile, status: 'completed', result: data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      setFiles(prev => prev.map(f => 
        f.id === batchFile.id ? { ...f, status: 'error', error: errorMessage } : f
      ));
      return { ...batchFile, status: 'error', error: errorMessage };
    }
  };

  const processAllFiles = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) {
      toast.error("Keine Dateien zum Verarbeiten");
      return;
    }

    setIsProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    // Process files sequentially to avoid rate limits
    for (const file of pendingFiles) {
      const result = await processFile(file);
      if (result.status === 'completed') {
        successCount++;
      } else {
        errorCount++;
      }
      // Small delay between files
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsProcessing(false);
    onUsageUpdate();

    if (errorCount === 0) {
      toast.success(`Alle ${successCount} Dokumente erfolgreich analysiert!`);
    } else {
      toast.warning(`${successCount} erfolgreich, ${errorCount} fehlgeschlagen`);
    }
  };

  const exportSeparatePDFs = () => {
    const completedFiles = files.filter(f => f.status === 'completed' && f.result);
    completedFiles.forEach(f => {
      exportAnalysisToPDF({
        fileName: f.file.name,
        summary: f.result!.zusammenfassung,
        keywords: f.result!.keywords,
        sentiment: f.result!.sentiment,
        themes: f.result!.themes,
        language: f.result!.language
      });
    });
    toast.success(`${completedFiles.length} separate PDF(s) heruntergeladen`);
  };

  const exportCombinedPDF = async () => {
    const completedFiles = files.filter(f => f.status === 'completed' && f.result);
    const analyses = completedFiles.map(f => ({
      fileName: f.file.name,
      summary: f.result!.zusammenfassung,
      keywords: f.result!.keywords,
      sentiment: f.result!.sentiment,
      themes: f.result!.themes,
      language: f.result!.language
    }));
    await exportBatchAnalysesToPDF(analyses);
    toast.success(`Kombiniertes PDF mit ${completedFiles.length} Analysen heruntergeladen`);
  };

  const completedCount = files.filter(f => f.status === 'completed').length;
  const pendingCount = files.filter(f => f.status === 'pending').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Upload Card */}
      <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Upload className="w-5 h-5 text-primary" />
            Mehrere Dateien hochladen
          </CardTitle>
          <CardDescription className="text-slate-600">
            Laden Sie mehrere PDF oder TXT Dateien gleichzeitig hoch
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* File Input */}
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-primary/50 transition-colors">
            <input
              type="file"
              accept=".txt,.md,.csv,.json,.pdf"
              multiple
              onChange={handleFilesSelect}
              className="hidden"
              id="batch-file-input"
              disabled={isProcessing}
            />
            <label 
              htmlFor="batch-file-input" 
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              <Upload className="w-10 h-10 text-slate-400" />
              <span className="text-sm text-slate-600">
                Klicken zum Hochladen oder Dateien hierher ziehen
              </span>
              <span className="text-xs text-slate-400">
                PDF, TXT, MD, CSV, JSON
              </span>
            </label>
          </div>

          {/* Summary Length */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Zusammenfassungslänge</label>
            <Select value={summaryLength} onValueChange={(v: SummaryLength) => setSummaryLength(v)}>
              <SelectTrigger className="border-slate-200">
                <SelectValue placeholder="Länge wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="short">Kurz (50-100 Wörter)</SelectItem>
                <SelectItem value="medium">Mittel (150-200 Wörter)</SelectItem>
                <SelectItem value="detailed">Ausführlich (300-500 Wörter)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {files.map(f => (
                <div 
                  key={f.id} 
                  className={`flex items-center gap-3 p-3 rounded-xl border ${
                    f.status === 'completed' ? 'bg-green-50 border-green-200' :
                    f.status === 'error' ? 'bg-red-50 border-red-200' :
                    f.status === 'processing' ? 'bg-blue-50 border-blue-200' :
                    'bg-slate-50 border-slate-200'
                  }`}
                >
                  <FileText className={`w-4 h-4 ${
                    f.status === 'completed' ? 'text-green-600' :
                    f.status === 'error' ? 'text-red-600' :
                    f.status === 'processing' ? 'text-blue-600' :
                    'text-slate-500'
                  }`} />
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">
                      {f.file.name}
                    </p>
                    {f.status === 'processing' && (
                      <Progress value={f.progress} className="h-1 mt-1" />
                    )}
                    {f.status === 'error' && (
                      <p className="text-xs text-red-600 truncate">{f.error}</p>
                    )}
                  </div>

                  {f.status === 'completed' && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                  {f.status === 'error' && <XCircle className="w-4 h-4 text-red-600" />}
                  {f.status === 'processing' && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
                  
                  {f.status === 'pending' && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={() => removeFile(f.id)}
                    >
                      <Trash2 className="h-3 w-3 text-slate-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Stats */}
          {files.length > 0 && (
            <div className="flex gap-2 text-xs">
              <Badge variant="secondary" className="rounded-full bg-slate-100">
                {pendingCount} wartend
              </Badge>
              <Badge variant="secondary" className="rounded-full bg-green-100 text-green-700">
                {completedCount} fertig
              </Badge>
              {errorCount > 0 && (
                <Badge variant="secondary" className="rounded-full bg-red-100 text-red-700">
                  {errorCount} Fehler
                </Badge>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              onClick={processAllFiles}
              disabled={isProcessing || pendingCount === 0}
              className="flex-1 rounded-xl bg-gradient-to-r from-primary to-blue-600 hover:shadow-lg transition-all"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verarbeite...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Alle analysieren ({pendingCount})
                </>
              )}
            </Button>
            {files.length > 0 && (
              <Button
                variant="outline"
                onClick={clearAll}
                disabled={isProcessing}
                className="rounded-xl"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Card */}
      <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              Ergebnisse
            </CardTitle>
            {completedCount > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="rounded-xl">
                    <Download className="h-4 w-4 mr-1" />
                    Exportieren
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportCombinedPDF}>
                    <FileStack className="h-4 w-4 mr-2" />
                    Ein kombiniertes PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportSeparatePDFs}>
                    <Files className="h-4 w-4 mr-2" />
                    Separate PDFs ({completedCount})
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {completedCount > 0 ? (
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {files.filter(f => f.status === 'completed' && f.result).map(f => (
                <div 
                  key={f.id} 
                  className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-slate-900 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      {f.file.name}
                    </h4>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      onClick={() => {
                        exportAnalysisToPDF({
                          fileName: f.file.name,
                          summary: f.result!.zusammenfassung,
                          keywords: f.result!.keywords,
                          sentiment: f.result!.sentiment,
                          themes: f.result!.themes,
                          language: f.result!.language
                        });
                        toast.success("PDF heruntergeladen");
                      }}
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Category */}
                  {f.result!.category && (
                    <Badge className="rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200">
                      <FolderOpen className="h-3 w-3 mr-1" />
                      {f.result!.category}
                    </Badge>
                  )}

                  {/* Summary */}
                  <p className="text-sm text-slate-600 line-clamp-3">
                    {f.result!.zusammenfassung}
                  </p>

                  {/* Keywords */}
                  {f.result!.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {f.result!.keywords.slice(0, 5).map((kw, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs rounded-full bg-blue-100 text-primary">
                          {kw}
                        </Badge>
                      ))}
                      {f.result!.keywords.length > 5 && (
                        <Badge variant="secondary" className="text-xs rounded-full bg-slate-100">
                          +{f.result!.keywords.length - 5}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Sentiment Mini */}
                  {f.result!.sentiment && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-500">Sentiment:</span>
                      <Badge variant="outline" className={`rounded-full ${
                        f.result!.sentiment.overall === 'positiv' ? 'border-green-300 bg-green-50 text-green-700' :
                        f.result!.sentiment.overall === 'negativ' ? 'border-red-300 bg-red-50 text-red-700' :
                        'border-slate-300 bg-slate-50 text-slate-600'
                      }`}>
                        {f.result!.sentiment.overall}
                      </Badge>
                    </div>
                  )}

                  {/* Confidentiality Warnings */}
                  {f.result!.confidentialityWarnings && f.result!.confidentialityWarnings.length > 0 && (
                    <Alert className="border-amber-200 bg-amber-50 py-2">
                      <AlertTriangle className="h-3 w-3 text-amber-600" />
                      <AlertDescription className="text-xs text-amber-700">
                        <Shield className="h-3 w-3 inline mr-1" />
                        {f.result!.confidentialityWarnings.length} Vertraulichkeitshinweis(e)
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-center text-slate-400">
              <FileText className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">Hier erscheinen Ihre Ergebnisse</p>
              <p className="text-xs">Laden Sie Dateien hoch und starten Sie die Analyse</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
